import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Increase timeout to handle Railway cold starts (can take 10-30s)
  timeout: 60000, // 60 seconds
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  // Use customer token for customer-auth routes, admin token for others
  const isCustomerRoute = config.url?.includes('/customer-auth');
  const token = isCustomerRoute
    ? localStorage.getItem('customerToken')
    : localStorage.getItem('token');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  verify: () => api.get('/auth/verify'),
  changePassword: (data) => api.post('/auth/change-password', data),
  changeUsername: (data) => api.post('/auth/change-username', data),
};

// Services API
export const servicesAPI = {
  getAll: () => api.get('/services'),
  getById: (id) => api.get(`/services/${id}`),
  create: (service) => api.post('/services', service),
  update: (id, service) => api.put(`/services/${id}`, service),
  delete: (id) => api.delete(`/services/${id}`),
  bulkImport: (services) => api.post('/services/bulk-import', { services }),
  reorder: (serviceIds) => api.post('/services/reorder', { serviceIds }),
};

// Time Slots API
export const timeSlotsAPI = {
  getAvailable: (date, duration) =>
    api.get('/time-slots/available', { params: { date, duration } }),
  getAll: (date) => api.get('/time-slots', { params: { date } }),
  create: (slot) => api.post('/time-slots', slot),
  createBulk: (data) => api.post('/time-slots/bulk', data),
  update: (id, slot) => api.put(`/time-slots/${id}`, slot),
  delete: (id) => api.delete(`/time-slots/${id}`),
  // Templates
  getTemplates: () => api.get('/time-slots/templates'),
  createTemplate: (template) => api.post('/time-slots/templates', template),
  updateTemplate: (id, template) => api.put(`/time-slots/templates/${id}`, template),
  deleteTemplate: (id) => api.delete(`/time-slots/templates/${id}`),
  generateFromTemplates: (data) => api.post('/time-slots/templates/generate', data),
  // Exceptions
  getExceptions: () => api.get('/time-slots/exceptions'),
  createException: (exception) => api.post('/time-slots/exceptions', exception),
  deleteException: (id) => api.delete(`/time-slots/exceptions/${id}`),
};

// Bookings API
export const bookingsAPI = {
  getAll: (filters) => api.get('/bookings', { params: filters }),
  getById: (id) => api.get(`/bookings/${id}`),
  getUpcoming: () => api.get('/bookings/upcoming'),
  checkCard: (email) => api.get('/bookings/check-card', { params: { email } }),
  create: (booking) => api.post('/bookings', booking),
  adminCreate: (booking) => api.post('/bookings/admin-create', booking),
  adminCreateRecurring: (booking) => api.post('/bookings/admin-create-recurring', booking),
  completePayment: (id, paymentMethodId) =>
    api.post(`/bookings/${id}/payment`, { payment_method_id: paymentMethodId }),
  saveCard: (id, data) => api.post(`/bookings/${id}/save-card`, data),
  chargeNoshow: (id, data) => api.post(`/bookings/${id}/charge-noshow`, data),
  recordCashPayment: (id) => api.post(`/bookings/${id}/cash-payment`),
  updateStatus: (id, status) => api.patch(`/bookings/${id}/status`, { status }),
  approveBooking: (id) => api.post(`/bookings/${id}/approve`),
  rejectBooking: (id, data) => api.post(`/bookings/${id}/reject`, data),
  delete: (id) => api.delete(`/bookings/${id}`),
  getCustomerHistory: (customerId) => api.get(`/bookings/customer/${customerId}/history`),
  getAllCustomers: () => api.get('/bookings/customers'),
  searchCustomers: (query) => api.get('/bookings/customers/search', { params: { q: query } }),
  updateCustomerNotes: (id, data) => api.put(`/bookings/customers/${id}/notes`, data),
  deleteCustomer: (id) => api.delete(`/bookings/customers/${id}`),
};

// Reports API
export const reportsAPI = {
  getTransactions: (filters) => api.get('/reports/transactions', { params: filters }),
  getRevenue: (filters) => api.get('/reports/revenue', { params: filters }),
  getServicesPerformance: (filters) =>
    api.get('/reports/services-performance', { params: filters }),
  getBookingsStats: (filters) =>
    api.get('/reports/bookings-stats', { params: filters }),
  getDailyRevenue: (filters) => api.get('/reports/daily-revenue', { params: filters }),
};

// Messages API
export const messagesAPI = {
  getConversations: () => api.get('/messages/conversations'),
  getCustomerMessages: (customerId) => api.get(`/messages/customer/${customerId}`),
  send: (data) => api.post('/messages/send', data),
  markAsRead: (id) => api.patch(`/messages/${id}/read`),
  // Templates
  getTemplates: () => api.get('/messages/templates'),
  createTemplate: (template) => api.post('/messages/templates', template),
  updateTemplate: (id, template) => api.put(`/messages/templates/${id}`, template),
  deleteTemplate: (id) => api.delete(`/messages/templates/${id}`),
};

// Customer Auth API (for customer portal)
export const customerAuthAPI = {
  register: (data) => api.post('/customer-auth/register', data),
  login: (data) => api.post('/customer-auth/login', data),
  sendMagicLink: (email) => api.post('/customer-auth/magic-link', { email }),
  verifyMagicLink: (token) => api.post('/customer-auth/verify-magic-link', { token }),
  forgotPassword: (email) => api.post('/customer-auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/customer-auth/reset-password', data),
  getProfile: () => api.get('/customer-auth/me'),
  updateProfile: (data) => api.put('/customer-auth/profile', data),
  changePassword: (data) => api.post('/customer-auth/change-password', data),
  // Booking requests (cancellations and amendments)
  submitBookingRequest: (data) => api.post('/customer-auth/booking-request', data),
  getBookingRequests: () => api.get('/customer-auth/booking-requests'),
  cancelBookingRequest: (id) => api.delete(`/customer-auth/booking-request/${id}`),
};

// Booking Requests API (for admin)
export const bookingRequestsAPI = {
  getAll: (status = 'pending') => api.get('/bookings/requests', { params: { status } }),
  approve: (id, data) => api.post(`/bookings/requests/${id}/approve`, data),
  approveWithCharge: (id, data) => api.post(`/bookings/requests/${id}/approve-with-charge`, data),
  reject: (id, data) => api.post(`/bookings/requests/${id}/reject`, data),
};

// Discount Codes API (promotional codes for social media)
export const discountCodesAPI = {
  // Admin endpoints
  getAll: () => api.get('/discount-codes'),
  getById: (id) => api.get(`/discount-codes/${id}`),
  create: (data) => api.post('/discount-codes', data),
  update: (id, data) => api.put(`/discount-codes/${id}`, data),
  delete: (id) => api.delete(`/discount-codes/${id}`),
  generate: () => api.post('/discount-codes/generate'),
  // Public endpoint (for booking flow)
  validate: (data) => api.post('/discount-codes/validate', data),
};

// Site Settings API (about, hours, Instagram, etc.)
export const siteSettingsAPI = {
  getAll: () => api.get('/site-settings'),
  update: (settings) => api.put('/site-settings', settings),
  updateSingle: (key, value) => api.put(`/site-settings/${key}`, { value }),
};

// Reviews API
export const reviewsAPI = {
  // Public
  getAll: (limit = 10) => api.get('/reviews', { params: { limit } }),
  submit: (data) => api.post('/reviews', data),
  // Admin
  getAllAdmin: () => api.get('/reviews/admin'),
  toggle: (id) => api.patch(`/reviews/${id}/toggle`),
  delete: (id) => api.delete(`/reviews/${id}`),
  importFresha: () => api.post('/reviews/import-fresha'),
};

// Loyalty API (stamp card system - per service category)
export const loyaltyAPI = {
  // Customer endpoints (use customer token)
  getMyStatus: () => {
    const token = localStorage.getItem('customerToken');
    return api.get('/loyalty/my-status', {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  getMyRewards: () => {
    const token = localStorage.getItem('customerToken');
    return api.get('/loyalty/my-rewards', {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  redeemReward: (category) => {
    const token = localStorage.getItem('customerToken');
    return api.post(`/loyalty/redeem/${encodeURIComponent(category)}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  getMyHistory: () => {
    const token = localStorage.getItem('customerToken');
    return api.get('/loyalty/my-history', {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  // Admin endpoints (use admin token)
  getConfig: () => api.get('/loyalty/config'),
  updateConfig: (data) => api.put('/loyalty/config', data),
  getCategories: () => api.get('/loyalty/categories'),
  getCustomers: () => api.get('/loyalty/customers'),
  getStats: () => api.get('/loyalty/stats'),
  adjustStamps: (customerId, data) => api.post(`/loyalty/adjust/${customerId}`, data),
  validateCode: (data) => api.post('/loyalty/validate-code', data),
};

export default api;
